import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit} from "@angular/core";
import {NavigationService} from "../../orchestration/navigation.service";
import {AnalyticsService} from "../../orchestration/analytics.service";
import {NotificationService} from "../../notification/notification.service";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {ButtonProminence} from "../../buttons/button/button";
import {Page} from "../../orchestration/page";
import {ReorderStatus} from "../reorder-status";
import {GroupOrderModel} from "../../group-order/group-order.model";
import {IdentityModel} from "../../identity/identity.model";
import {OrderModel} from "../../order/order.model";
import {OrderingContextModel} from "../../ordering-context/ordering-context.model";
import {OrderingContextController} from "../../ordering-context/ordering-context.controller";
import {OrderEvents, OrderInitiationStatus, OrderSubmissionStatus} from "../../order/order-events";
import {ValidationMessage} from "@fedelta-pos/data-client";
import {Subscription} from "rxjs";
import {RecentOrderData, RecentOrderService} from "../../order/recent-order.service";
import {PaymentType} from "../../payment/payment";
import {DisplayableOrderItem, OrderItem} from "../../order/order";
import {PaymentEvents} from "../../payment/payment-events";

@Component({
    selector: "woa-reorder-page",
    templateUrl: "reorder-page.component.html",
    providers: [{provide: Page, useExisting: ReorderPageComponent}]
})

export class ReorderPageComponent extends Page implements OnInit, AfterViewInit, OnDestroy {
    ButtonProminence = ButtonProminence;

    paying: boolean = false;

    private subscriptions: Subscription = new Subscription();
    private addedOrderItems: OrderItem[];
    private orderValidForCheckout: boolean = false;
    private quickPayConfirmed: boolean = false;
    private recentOrder: RecentOrderData;
    private hasPreOrderItems: boolean;
    private groupOrderValidationMessages: ValidationMessage[];
    private groupOrderError: any;

    constructor(recentOrderService: RecentOrderService,
                private groupOrderModel: GroupOrderModel,
                private identityModel: IdentityModel,
                private orderingContextController: OrderingContextController,
                private notificationService: NotificationService,
                private navigationService: NavigationService,
                private analyticsService: AnalyticsService,
                private loadingService: LoadingService,
                private changeDetectorRef: ChangeDetectorRef,
                public orderingContextModel: OrderingContextModel,
                public orderModel: OrderModel) {
        super();
        this.recentOrder = recentOrderService.recentOrders[0];
    }

    ngOnInit(): void {
        this.subscriptions.add(OrderEvents.orderInitiationStatus$.subscribe(async (status: OrderInitiationStatus) => {
            if (status === OrderInitiationStatus.INITIATED) {
                if (this.notificationService.alertDisplayed$.value) {
                    this.orderValidForCheckout = false;
                } else {
                    const quickPayType: PaymentType = this.orderModel.getQuickPayType();
                    if (quickPayType === undefined) {
                        this.orderValidForCheckout = false;
                    } else {
                        await this.orderingContextController.untilExpectedCompletionEvaluationComplete();
                        this.orderValidForCheckout = this.orderModel.validateOrderForPayment(quickPayType, false) && !this.orderingContextModel.fulfilmentDetailsRequired;
                    }
                }
            } else {
                this.orderValidForCheckout = false;
            }
            this.changeDetectorRef.detectChanges();
        }));
        this.subscriptions.add(OrderEvents.orderSubmissionStatus$.subscribe(async (status: OrderSubmissionStatus) => {
            if (status === OrderSubmissionStatus.COMPLETE) {
                return this.navigationService.dismissPage(this, new ReorderStatus(true, this.quickPayConfirmed));
            }
            if (status === OrderSubmissionStatus.ORDER_INVALID || status === OrderSubmissionStatus.ORDER_CANCELLED) {
                return this.navigationService.dismissPage(this, new ReorderStatus(false, false));
            }
        }));
        this.subscriptions.add(OrderEvents.orderInitiationRequestReceived$.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(PaymentEvents.isSubmittingWalletPayment$.subscribe(() => this.changeDetectorRef.detectChanges()));

        // noinspection JSIgnoredPromiseFromCall
        this.load();
    }

    ngAfterViewInit(): void {
        if (!this.orderingContextModel.firstName) {
            this.orderingContextModel.firstName = this.recentOrder.orderPlacement.firstName;
            this.orderingContextModel.firstNameTrusted = this.recentOrder.orderPlacement.nameTrusted;
        }
        if (!this.orderingContextModel.lastName) {
            this.orderingContextModel.lastName = this.recentOrder.orderPlacement.lastName;
            this.orderingContextModel.lastNameTrusted = this.recentOrder.orderPlacement.nameTrusted;
        }
        if (!this.orderingContextModel.email) {
            this.orderingContextModel.email = this.recentOrder.orderPlacement.email;
            this.orderingContextModel.emailTrusted = this.recentOrder.orderPlacement.emailTrusted;
        }
        if (!this.orderingContextModel.mobilePhone) {
            this.orderingContextModel.mobilePhone = this.recentOrder.orderPlacement.mobilePhone;
            this.orderingContextModel.mobilePhoneTrusted = this.recentOrder.orderPlacement.mobilePhoneTrusted;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get itemTotal(): number {
        return this.addedOrderItems ? this.addedOrderItems.map((item: OrderItem) => item.localTotal).reduce((amount: number, total: number) => total + amount, 0.0) : 0.0;
    }

    get showQuickPay(): boolean {
        return !this.paying && !this.hasPreOrderItems && this.orderValidForCheckout;
    }

    onDidDismissPage(status: ReorderStatus): void {
        if (!(status && status.ordered)) {
            if (this.orderModel.hasOrderedItems()) {
                this.orderModel.removeOrderItemsByIdentifier(this.addedOrderItems.map((item) => item.identifier));
                this.addedOrderItems = undefined;
            }
            if (!status) {
                // noinspection JSIgnoredPromiseFromCall
                this.analyticsService.trackReorderRoundCancelled(this.itemTotal);
            }
        }
    }

    private async load(): Promise<void> {
        try {
            await this.loadingService.performWithSpinner(async () => {
                this.hasPreOrderItems = this.orderModel.hasOrderedItems();
                this.addedOrderItems = this.orderModel.getReorderableOrderItems()
                    .map((orderItem: OrderItem) => {
                        orderItem.proposed = true;
                        orderItem.deal = false;
                        return orderItem;
                    })
                    .sort((a: OrderItem, b: OrderItem) => (a.transferRequestName || "").localeCompare(b.transferRequestName || ""));
                this.orderModel.addOrderItems(this.addedOrderItems);
                await this.analyticsService.trackReorderRoundShown(this.orderModel.showQuickPay(), this.itemTotal);
            });
        } catch (error) {
            console.log(error);
        }
    }

    async addToOrder(): Promise<void> {
        this.confirmOrderedItems(false);
        if (!!this.orderModel.totalTransfers && !this.groupOrderModel.hasGroupOrder) {
            await this.reinitiateGroupOrder();
        }
        // noinspection ES6MissingAwait
        this.analyticsService.trackReorderRoundAdded(this.itemTotal);
        // noinspection ES6MissingAwait
        this.navigationService.dismissPage(this, new ReorderStatus(true, false, this.recentOrder.orderInitiation.transferTargetName, this.groupOrderValidationMessages, this.groupOrderError));
    }

    async quickPay(paymentType: PaymentType): Promise<void> {
        if (this.orderModel.initiationStatus !== OrderInitiationStatus.INITIATED) {
            return;
        }
        this.paying = true;
        this.changeDetectorRef.detectChanges();
        await this.loadingService.performWithSpinner(async () => {
            this.confirmOrderedItems(true);
            await this.orderModel.untilOrderInitiationComplete();
        }, 30000);
        const paid: boolean = await this.orderModel.placeOrder(paymentType, this.groupOrderModel.preferredTransferTarget);
        if (paid) {
            // noinspection ES6MissingAwait
            this.analyticsService.trackReorderRoundPaid(this.itemTotal);
        } else {
            this.paying = false;
            this.changeDetectorRef.detectChanges();
        }
    }

    private confirmOrderedItems(quickPay: boolean): void {
        this.quickPayConfirmed = quickPay;

        // Removed proposed flag from new items, and set justOrdered flag to trigger promos
        this.addedOrderItems.forEach((item: OrderItem) => {
            item.justOrdered = !quickPay;
            if (item.justOrdered) {
                // This will keep the item marked as justOrdered when initiation completes
                item.initiatedTotal = undefined;
            }
            item.proposed = false;
        });

        // Request order initiation to ensure locally persisted order is up-to-date
        this.orderModel.requestOrderInitiation("reorder");
    }

    private async reinitiateGroupOrder(): Promise<void> {
        await this.loadingService.performWithSpinner(async () => {
            try {
                this.groupOrderValidationMessages = await this.groupOrderModel.saveGroupOrder(true, this.recentOrder.orderInitiation.transferTargetName || this.identityModel.currentIdentity.firstName);
            } catch (error) {
                this.groupOrderError = error;
            }
        }, undefined, "woa.message.group-order.creating");
    }

    async removeItem(orderItem: DisplayableOrderItem): Promise<any> {
        if (await this.orderModel.removeOrderItem(orderItem)) {
            this.addedOrderItems = this.addedOrderItems.filter((item: OrderItem) => item !== orderItem);
            if (!this.addedOrderItems.length) {
                return this.navigationService.dismissPage(this);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/ordering-page/reorder-page/reorder-page.component.ts